import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Lightning, ArrowLeft, Users, Coins, RocketLaunch, CircleNotch, Crown, ChartBar,
} from "@phosphor-icons/react";
import GalaxyBackground from "../components/GalaxyBackground";

const PLAN_STYLE = {
  owner: "text-forge-primary border-forge-primary/40 bg-forge-primary/10",
  pro: "text-purple-300 border-purple-400/30 bg-purple-400/10",
  standard: "text-blue-300 border-blue-400/30 bg-blue-400/10",
  free: "text-neutral-400 border-forge-border bg-forge-hover",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-forge-border bg-forge-surface rounded-sm p-5">
      <div className="flex items-center gap-2 text-neutral-400 mb-3">
        <Icon size={16} className="text-forge-primary" />
        <span className="uppercase text-[10px] tracking-[0.2em]">{label}</span>
      </div>
      <div className="text-3xl font-light">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate("/");
      return;
    }
    Promise.all([api.get("/api/admin/users"), api.get("/api/admin/stats")])
      .then(([u, s]) => { setUsers(u.data); setStats(s.data); })
      .catch(() => toast.error("Failed to load admin data"))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-forge-bg text-white relative noise-overlay">
      <div className="fixed inset-0 z-0 opacity-30 pointer-events-none"><GalaxyBackground density={0.6} /></div>
      <div className="fixed inset-0 z-0 pointer-events-none bg-gradient-to-b from-forge-bg/50 via-forge-bg/75 to-forge-bg" />

      <header className="relative z-10 border-b border-forge-border bg-forge-bg">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="admin-back" onClick={() => navigate("/")} className="text-neutral-400 hover:text-white transition-colors"><ArrowLeft size={18} /></button>
            <Lightning size={22} weight="fill" className="text-forge-primary" />
            <span className="font-mono text-lg tracking-tight">Matrix</span>
            <span className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-forge-primary border border-forge-primary/40 px-2 py-0.5 rounded-sm"><Crown size={11} weight="fill" /> Owner</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 py-12">
        <p className="uppercase text-xs tracking-[0.2em] text-forge-primary mb-3">Owner console</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-10">Subscribers & platform</h1>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm"><CircleNotch size={18} className="animate-spin text-forge-primary" /> Loading...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <StatCard icon={Users} label="Total users" value={stats?.total_users ?? 0} />
              <StatCard icon={Coins} label="Paying subs" value={stats?.paying_subscribers ?? 0} />
              <StatCard icon={ChartBar} label="Projects" value={stats?.total_projects ?? 0} />
              <StatCard icon={RocketLaunch} label="Published" value={stats?.published_apps ?? 0} />
            </div>

            <div className="flex items-center gap-3 mb-5">
              <Users size={20} className="text-forge-primary" />
              <h2 className="text-lg font-medium tracking-tight">All subscribers</h2>
              <span className="text-sm text-neutral-500 font-mono">{users.length}</span>
            </div>

            <div className="border border-forge-border rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="subscribers-table">
                <thead>
                  <tr className="bg-forge-surface border-b border-forge-border text-left">
                    <th className="px-4 py-3 uppercase text-[10px] tracking-[0.15em] text-neutral-500 font-medium">User</th>
                    <th className="px-4 py-3 uppercase text-[10px] tracking-[0.15em] text-neutral-500 font-medium">Plan</th>
                    <th className="px-4 py-3 uppercase text-[10px] tracking-[0.15em] text-neutral-500 font-medium">Credits</th>
                    <th className="px-4 py-3 uppercase text-[10px] tracking-[0.15em] text-neutral-500 font-medium">Projects</th>
                    <th className="px-4 py-3 uppercase text-[10px] tracking-[0.15em] text-neutral-500 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-forge-border last:border-0 hover:bg-forge-surface/60 transition-colors" data-testid={`subscriber-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{u.name || u.email.split("@")[0]}</div>
                        <div className="text-xs text-neutral-500 font-mono">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${PLAN_STYLE[u.plan] || PLAN_STYLE.free}`}>{u.plan}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-neutral-300">{u.plan === "owner" ? "∞" : (u.credits + u.topup_credits)}</td>
                      <td className="px-4 py-3 font-mono text-neutral-300">{u.project_count}</td>
                      <td className="px-4 py-3 text-neutral-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
